import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel";
import { TaskExecutionPanel } from "../components/TaskExecutionPanel";
import {
  clearQaRun,
  claimQaTask,
  defaultQaPersona,
  evaluateQaGate,
  getQaRun,
  listQaRuns,
  qaRoutes,
  resolveQaReview,
  startQaRun,
  submitQaProof,
} from "../lib/adminQaService";
import { payoutModelLabel } from "../lib/compensation";
import { listLaunchFlags } from "../lib/operationsService";
import { finalizeQaTask, getAdminQaActiveTask, listTaskOffers } from "../lib/workflowService";
import type { QaGateResult, QaPersona, QaRun, TaskOffer, WorkRoute } from "../lib/workflowTypes";
import "../member-final.css";

type QaTab = "member" | "task" | "review" | "money";

export function AdminQaPage() {
  const client = useQueryClient();
  const [params] = useSearchParams();
  const [persona, setPersona] = useState<QaPersona>(defaultQaPersona);
  const [tab, setTab] = useState<QaTab>("member");
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [currentRun, setCurrentRun] = useState<QaRun | null>(null);
  const [evidence, setEvidence] = useState<Record<string, string>>({});
  const [reviewNotes, setReviewNotes] = useState("QA proof matches the SOP and stays internal only.");

  const offers = useQuery({ queryKey: ["task-offers"], queryFn: listTaskOffers });
  const flags = useQuery({ queryKey: ["launch-flags"], queryFn: listLaunchFlags });
  const runs = useQuery({ queryKey: ["admin-qa-runs"], queryFn: listQaRuns });
  const linkedRunId = params.get("run") ?? "";
  const linkedRun = useQuery({
    queryKey: ["admin-qa-run", linkedRunId],
    enabled: Boolean(linkedRunId),
    queryFn: () => getQaRun(linkedRunId),
  });
  const paidMissionsOpen = Boolean(flags.data?.find((flag) => flag.key === "paid_missions_open")?.enabled);
  const selectedOffer = (offers.data ?? []).find((offer) => offer.id === selectedOfferId) ?? offers.data?.[0];
  const gateMap = useMemo(() => new Map((offers.data ?? []).map((offer) => [
    offer.id,
    evaluateQaGate(offer, persona, paidMissionsOpen),
  ])), [offers.data, paidMissionsOpen, persona]);
  const selectedGate = selectedOffer ? gateMap.get(selectedOffer.id) : undefined;
  const qaActiveTask = useQuery({
    queryKey: ["admin-qa-active-task", currentRun?.id],
    enabled: Boolean(currentRun?.id && currentRun.assignmentId && ["claimed", "submitted"].includes(currentRun.state)),
    queryFn: () => getAdminQaActiveTask(currentRun!.id),
  });

  useEffect(() => {
    if (linkedRun.data) {
      setCurrentRun(linkedRun.data);
      setSelectedOfferId(linkedRun.data.offerId ?? "");
      setPersona(linkedRun.data.persona);
      setTab(linkedRun.data.state === "submitted" ? "review" : "task");
    }
  }, [linkedRun.data]);

  const startAndClaim = useMutation({
    mutationFn: async ({ offer, gate }: { offer: TaskOffer; gate: QaGateResult }) => {
      const run = await startQaRun(persona);
      return claimQaTask(run.id, offer.id, gate);
    },
    onSuccess: async (run, variables) => {
      setCurrentRun(run);
      setSelectedOfferId(run.offerId ?? "");
      setEvidence(defaultEvidence(variables.offer));
      setTab("task");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["admin-qa-runs"] }),
        client.invalidateQueries({ queryKey: ["admin-qa-active-task", run.id] }),
      ]);
    },
  });
  const submitProof = useMutation({
    mutationFn: () => {
      if (!currentRun) throw new Error("Start a QA run first.");
      return submitQaProof(currentRun.id, evidence);
    },
    onSuccess: async (run) => {
      setCurrentRun(run);
      setTab("review");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["admin-qa-runs"] }),
        client.invalidateQueries({ queryKey: ["admin-qa-active-task", run.id] }),
      ]);
    },
  });
  const resolveReview = useMutation({
    mutationFn: (decision: "approved" | "rejected") => {
      if (!currentRun) throw new Error("Submit QA proof first.");
      return resolveQaReview(currentRun.id, decision, reviewNotes);
    },
    onSuccess: async (run) => {
      setCurrentRun(run);
      setTab("money");
      await client.invalidateQueries({ queryKey: ["admin-qa-runs"] });
    },
  });
  const clearRun = useMutation({
    mutationFn: () => {
      if (!currentRun) throw new Error("Choose a QA run first.");
      return clearQaRun(currentRun.id);
    },
    onSuccess: async (run) => {
      setCurrentRun(run);
      await client.invalidateQueries({ queryKey: ["admin-qa-runs"] });
    },
  });
  const error = offers.error ?? flags.error ?? runs.error ?? startAndClaim.error ?? submitProof.error ?? resolveReview.error ?? clearRun.error;

  return (
    <div className="member-app standalone-member-page admin-qa-page">
      <header className="member-header">
        <Link className="member-logo" to="/admin"><span>H</span><b>ADMIN QA</b></Link>
        <div className="member-header-actions">
          <Link to="/admin/workflows">WORKFLOWS</Link>
          <Link to="/admin/cells">CELLS</Link>
          <Link to="/admin/payouts">PAYOUTS</Link>
          <Link to="/app">MEMBER APP</Link>
        </div>
      </header>
      <main>
        <section className="standalone-member-hero qa-hero">
          <span>QA ONLY · NO REAL PAYOUTS</span>
          <h1>Test the whole worker flow.</h1>
          <p>Pick a fake worker profile, see which repeatable tasks open, submit proof, approve it, and confirm the money stays not withdrawable.</p>
        </section>

        {error && <StatusPanel title="QA action failed" message={error.message} kind="error" />}

        <section className="qa-status-strip">
          <div><span>REAL PAID TASKS</span><b>{paidMissionsOpen ? "OPEN" : "CLOSED"}</b><small>{paidMissionsOpen ? "Workers can claim live paid tasks." : "Workers are blocked; QA can still dry-run."}</small></div>
          <div><span>QA RUN</span><b>{currentRun?.state.toUpperCase() ?? "NONE"}</b><small>{currentRun?.id.slice(0, 8) ?? "Start from Member View."}</small></div>
          <div><span>WOULD PAY</span><b>${(currentRun?.wouldPayUsd ?? selectedOffer?.rewardUsd ?? 0).toFixed(2)}</b><small>QA money is never withdrawable.</small></div>
        </section>

        <nav className="qa-tabs" aria-label="Admin QA steps">
          <button className={tab === "member" ? "active" : ""} onClick={() => setTab("member")}>Member View</button>
          <button className={tab === "task" ? "active" : ""} onClick={() => setTab("task")}>Task Flow</button>
          <button className={tab === "review" ? "active" : ""} onClick={() => setTab("review")}>Review Flow</button>
          <button className={tab === "money" ? "active" : ""} onClick={() => setTab("money")}>Money Flow</button>
        </nav>

        {tab === "member" && (
          <section className="qa-layout">
            <PersonaPanel persona={persona} onChange={setPersona} />
            <div className="qa-main-panel">
              <div className="member-section-head">
                <div><span>ADMIN PREVIEW</span><h2>Repeatable task board</h2></div>
                <p>This does not change your real profile. It only shows what a worker with these settings would see.</p>
              </div>
              <div className="qa-offer-list">
                {(offers.data ?? []).map((offer) => {
                  const gate = gateMap.get(offer.id)!;
                  return (
                    <article className={gate.qaRunnable ? "qa-offer-row ready" : "qa-offer-row blocked"} key={offer.id}>
                      <button onClick={() => { setSelectedOfferId(offer.id); setEvidence(defaultEvidence(offer)); }}>
                        <span>{gate.claimable ? "LIVE CLAIMABLE" : gate.qaRunnable ? "QA RUNNABLE" : "BLOCKED"}</span>
                        <b>{offer.title}</b>
                        <small>{offer.product.productName} · {offer.route.toUpperCase()} · ${offer.rewardUsd}</small>
                      </button>
                      <GateList gate={gate} compact />
                      <button
                        className="qa-run-button"
                        disabled={!gate.qaRunnable || startAndClaim.isPending}
                        onClick={() => startAndClaim.mutate({ offer, gate })}
                      >
                        START QA RUN
                      </button>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {tab === "task" && (
          <section className="qa-layout">
            <RunHistory runs={runs.data ?? []} />
            <div className="qa-main-panel">
              <div className="member-section-head">
                <div><span>INTERNAL QA ONLY</span><h2>Task claim and proof</h2></div>
                <p>Use this to test the worker action without creating a normal active task or payout.</p>
              </div>
              {currentRun && qaActiveTask.data ? (
                <TaskExecutionPanel
                  task={qaActiveTask.data}
                  qaOnly
                  submitLabel="SUBMIT QA PROOF"
                  releaseLabel="CLEAR QA RUN"
                  busy={clearRun.isPending}
                  onChanged={() => client.invalidateQueries({ queryKey: ["admin-qa-active-task", currentRun.id] })}
                  onSubmitted={async () => {
                    const next = await getQaRun(currentRun.id);
                    setCurrentRun(next);
                    setTab("review");
                    await Promise.all([
                      client.invalidateQueries({ queryKey: ["admin-qa-runs"] }),
                      client.invalidateQueries({ queryKey: ["admin-qa-active-task", currentRun.id] }),
                    ]);
                  }}
                  onReleased={() => clearRun.mutate()}
                  onSubmitTask={async (task, proof) => finalizeQaTask(currentRun.id, task.assignmentId, proof)}
                />
              ) : selectedOffer && selectedGate ? (
                <>
                  <TaskBrief offer={selectedOffer} gate={selectedGate} />
                  <div className="qa-proof-panel">
                    <h3>QA proof fields</h3>
                    {selectedOffer.evidenceRequirements.map((item) => (
                      <label key={item.key}>
                        <span>{item.label}</span>
                        <small>{item.help}</small>
                        <input
                          value={evidence[item.key] ?? ""}
                          onChange={(event) => setEvidence({ ...evidence, [item.key]: event.target.value })}
                          placeholder={item.kind === "url" ? "https://example.com/proof" : "QA proof value"}
                        />
                      </label>
                    ))}
                    <button
                      className="payout-submit"
                      disabled={!currentRun || currentRun.state !== "claimed" || !proofComplete(selectedOffer, evidence) || submitProof.isPending}
                      onClick={() => submitProof.mutate()}
                    >
                      SUBMIT QA PROOF
                    </button>
                  </div>
                </>
              ) : <EmptyQa title="No task selected." text="Choose a task in Member View first." />}
            </div>
          </section>
        )}

        {tab === "review" && (
          <section className="qa-layout">
            <RunHistory runs={runs.data ?? []} />
            <div className="qa-main-panel">
              <div className="member-section-head">
                <div><span>QA REVIEW QUEUE</span><h2>Review the proof</h2></div>
                <p>The normal approval button is replaced here so nobody mistakes QA approval for real payment approval.</p>
              </div>
              {currentRun && selectedOffer ? (
                <div className="qa-review-card">
                  <span>INTERNAL QA ONLY</span>
                  <h3>{selectedOffer.title}</h3>
                  <div className="qa-review-grid">
                    <div><b>Assignment</b><small>{currentRun.assignmentId ?? "Not claimed"}</small></div>
                    <div><b>Persona</b><small>{currentRun.persona.route} · L{currentRun.persona.level} · trust {currentRun.persona.trust}</small></div>
                    <div><b>Product</b><small>{selectedOffer.product.productName}</small></div>
                    <div><b>SOP</b><small>{selectedOffer.sopTitle} v{selectedOffer.sopVersion}</small></div>
                    <div><b>Would pay</b><small>${currentRun.wouldPayUsd.toFixed(2)} reversed QA earning</small></div>
                    <div><b>Review ID</b><small>{currentRun.reviewId ?? "Submit proof first"}</small></div>
                  </div>
                  <h4>Proof submitted</h4>
                  <ul>{Object.entries(currentRun.evidence).map(([key, value]) => <li key={key}><b>{key}</b><span>{value}</span></li>)}</ul>
                  <label>
                    <span>QA reviewer notes</span>
                    <textarea value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} />
                  </label>
                  <div className="member-actions">
                    <button className="primary" disabled={currentRun.state !== "submitted" || resolveReview.isPending} onClick={() => resolveReview.mutate("approved")}>APPROVE QA PROOF</button>
                    <button className="danger" disabled={currentRun.state !== "submitted" || reviewNotes.trim().length < 10 || resolveReview.isPending} onClick={() => resolveReview.mutate("rejected")}>REJECT QA PROOF</button>
                  </div>
                </div>
              ) : <EmptyQa title="No submitted QA proof." text="Claim a QA task and submit proof first." />}
            </div>
          </section>
        )}

        {tab === "money" && (
          <section className="qa-layout">
            <RunHistory runs={runs.data ?? []} />
            <div className="qa-main-panel">
              <div className="member-section-head">
                <div><span>QA MONEY FLOW</span><h2>Would pay, not withdrawable</h2></div>
                <p>This is the final MVP check: approval can calculate value, but QA earnings never become payout money.</p>
              </div>
              <div className="qa-money-grid">
                <div><span>WOULD PAY</span><strong>${(currentRun?.wouldPayUsd ?? 0).toFixed(2)}</strong><small>For approved proof only.</small></div>
                <div><span>PAYABLE</span><strong>$0.00</strong><small>QA earnings are excluded from payout summary.</small></div>
                <div><span>LEDGER STATUS</span><strong>REVERSED</strong><small>Live SQL marks QA-only rows reversed.</small></div>
                <div><span>PAYOUT REQUEST</span><strong>BLOCKED</strong><small>QA money cannot be requested.</small></div>
              </div>
              <StatusPanel
                title="Plain-English result"
                message={currentRun?.state === "approved"
                  ? "The task would pay after approval, but because this is QA, it creates no withdrawable money."
                  : "Approve QA proof to test the money calculation. It will still stay non-payable."}
                kind="success"
              />
              <div className="member-actions">
                <button disabled={!currentRun || clearRun.isPending} onClick={() => clearRun.mutate()}>CLEAR QA RUN</button>
                <Link className="qa-link-button" to="/app/earnings">OPEN REAL EARNINGS</Link>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function PersonaPanel({ persona, onChange }: { persona: QaPersona; onChange: (persona: QaPersona) => void }) {
  return (
    <aside className="qa-persona-panel">
      <span>QA PERSONA</span>
      <h2>Fake worker settings</h2>
      <label>
        <span>Route</span>
        <select value={persona.route} onChange={(event) => onChange({ ...persona, route: event.target.value as WorkRoute })}>
          {qaRoutes.map((route) => <option key={route} value={route}>{route}</option>)}
        </select>
      </label>
      <label>
        <span>Level</span>
        <input type="number" min={0} max={10} value={persona.level} onChange={(event) => onChange({ ...persona, level: Number(event.target.value) })} />
      </label>
      <label>
        <span>Trust</span>
        <input type="number" min={0} max={100} value={persona.trust} onChange={(event) => onChange({ ...persona, trust: Number(event.target.value) })} />
      </label>
      <label>
        <span>Active task state</span>
        <select value={persona.activeTaskState} onChange={(event) => onChange({ ...persona, activeTaskState: event.target.value as QaPersona["activeTaskState"] })}>
          <option value="none">none</option>
          <option value="active">active</option>
          <option value="submitted">submitted</option>
          <option value="changes_requested">changes requested</option>
        </select>
      </label>
      <label className="qa-check"><input type="checkbox" checked={persona.legalApproved} onChange={(event) => onChange({ ...persona, legalApproved: event.target.checked })} /> Legal approved</label>
      <label className="qa-check"><input type="checkbox" checked={persona.payoutEligible} onChange={(event) => onChange({ ...persona, payoutEligible: event.target.checked })} /> Payout eligible</label>
      <p>This preview never edits your real worker profile.</p>
    </aside>
  );
}

function TaskBrief({ offer, gate }: { offer: TaskOffer; gate: QaGateResult }) {
  return (
    <article className="qa-task-brief">
      <span>INTERNAL QA ONLY</span>
      <h3>{offer.title}</h3>
      <p>{offer.summary}</p>
      <div className="mission-product">
        <div><span>PRODUCT</span><b>{offer.product.productName}</b><small>{offer.product.offer}</small></div>
        <div><span>OBJECTIVE</span><b>{offer.product.objective}</b><small>{offer.product.targetCustomer}</small></div>
      </div>
      <div className="mission-preview-grid">
        <div><span>PAYOUT MODEL</span><b>{payoutModelLabel(offer.compensation, offer.rewardUsd)}</b></div>
        <div><span>QA GATE</span><b>{gate.claimable ? "Live claimable" : gate.qaRunnable ? "QA runnable" : "Blocked"}</b></div>
      </div>
      <GateList gate={gate} />
    </article>
  );
}

function GateList({ gate, compact = false }: { gate: QaGateResult; compact?: boolean }) {
  return (
    <ul className={compact ? "qa-gate-list compact" : "qa-gate-list"}>
      {gate.checks.map((check) => (
        <li className={check.passed ? "pass" : "fail"} key={check.key}>
          <b>{check.passed ? "PASS" : "BLOCK"}</b>
          <span>{check.label}</span>
          {!compact && <small>{check.reason}</small>}
        </li>
      ))}
    </ul>
  );
}

function RunHistory({ runs }: { runs: Awaited<ReturnType<typeof listQaRuns>> }) {
  return (
    <aside className="qa-persona-panel">
      <span>RECENT QA</span>
      <h2>Run history</h2>
      <div className="qa-run-history">
        {runs.slice(0, 5).map((run) => (
          <article key={run.id}>
            <b>{run.state.toUpperCase()}</b>
            <span>{run.route} · L{run.level} · trust {run.trust}</span>
            <small>${run.wouldPayUsd.toFixed(2)} · {new Date(run.createdAt).toLocaleString()}</small>
          </article>
        ))}
        {!runs.length && <p>No QA runs yet.</p>}
      </div>
    </aside>
  );
}

function EmptyQa({ title, text }: { title: string; text: string }) {
  return <div className="member-empty"><span>!</span><h3>{title}</h3><p>{text}</p></div>;
}

function defaultEvidence(offer?: TaskOffer) {
  if (!offer) return {};
  return Object.fromEntries(offer.evidenceRequirements.map((item) => [
    item.key,
    item.kind === "url" ? "https://example.com/internal-qa-proof" : `QA proof for ${item.label}`,
  ]));
}

function proofComplete(offer: TaskOffer, evidence: Record<string, string>) {
  return offer.evidenceRequirements.every((item) => !item.required || evidence[item.key]?.trim());
}
